import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenancy } from '@/contexts/TenancyContext';
import { useAuth } from '@/contexts/AuthContext';
import { onAuthReady } from '@/lib/authState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Search, RefreshCw, AlertCircle, ListFilter } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import AddAssuranceTaskModal from '../assurancelibrary/AddAssuranceTaskModal';
import { DuckLoadingScreen } from '@/components/DuckLoadingScreen';

// Import our service functions
import {
  getSOAUpdatesData,
  processControl,
  loadAssuranceTasks,
  getSupportingDocs,
  getAssignees,
  updateControlInSOA,
  sortObjectKeys,
  ControlData,
  AssuranceTask,
  SupportingDoc
} from '@/services/soaUpdatesService';
import { getAllTasks, AssuranceTask as AssuranceActionTask, createTask } from '@/services/assuranceActionsService';
import { getSuggestedTasks, acceptSuggestedTaskItem } from '@/services/aiSuggestionsService';
import { auth, FirebaseTimestamp } from '@/js/external-js/config/main_config';
import { getFrameworkInfo } from '@/utils/framework-utils';

// Import our new modular components
import { CellRenderer } from '@/components/update-cells/cell-renderer';
import { UpdateCellType, UpdateCellData, SuggestedAssuranceTask } from '@/components/update-cells/types';
import UnifiedTable from '@/components/update-cells/unified-table';
import { Download } from 'lucide-react';

// CSS for System Update
import './system-update.css';
import processAcceptedTask from './processAITask';

// Helper function to format dates from Firestore timestamps
const formatDate = (timestamp: any): string => {
  if (!timestamp || !timestamp.toDate) {
    return 'N/A';
  }

  try {
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

// Main System Update component
const SystemUpdate = () => {
  const { activeTenancy } = useTenancy();
  const { user, isAuthReady } = useAuth();
  const queryClient = useQueryClient();
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  // Component state
  const [currentBatch, setCurrentBatch] = useState(0);
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});
  const [implementationPlans, setImplementationPlans] = useState<Record<string, string>>({});
  const [assuranceTasks, setAssuranceTasks] = useState<Record<string, AssuranceTask[]>>({});
  const [supportingDocs, setSupportingDocs] = useState<Record<string, SupportingDoc[]>>({});
  const [assignees, setAssignees] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [suggestedTasks, setSuggestedTasks] = useState<SuggestedAssuranceTask[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Loading states for staged loading
  const [aiTasksLoaded, setAiTasksLoaded] = useState(false);
  const [controlsLoaded, setControlsLoaded] = useState(false);
  const [showInitialLoading, setShowInitialLoading] = useState(true);

  // State for accepting AI suggestions
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [acceptingTask, setAcceptingTask] = useState<{
    suggestionDocId: string;
    task: any;
  } | null>(null);

  // Fetch assurance tasks with active status
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [activeTasks, setActiveTasks] = useState<Array<AssuranceActionTask>>([]);

  const fetchAssuranceTasks = useCallback(async () => {
    if (!activeTenancy?.id || !isAuthInitialized || !isAuthReady || !user) return;

    setLoadingTasks(true);

    try {
      const allTasks = await getAllTasks();
      console.log(`[DEBUG] Retrieved ${allTasks.length} total assurance tasks`);

      // Find active tasks
      const activeTasks = allTasks.filter(
        task => task.checkDetail?.active === 'true'
      );

      setActiveTasks(activeTasks);
      console.log(`[DEBUG] Found ${activeTasks.length} active assurance tasks`);
    } catch (error) {
      console.error('[DEBUG] Error fetching assurance tasks:', error);
      setActiveTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, [activeTenancy?.id, isAuthInitialized, isAuthReady, user]);

  // Define max entries configuration for each module type
  const maxEntriesConfig = {
    [UpdateCellType.SOA_CONTROL]: 5, // Show max 5 controls, matching current behavior
    [UpdateCellType.ASSURANCE_TASK]: 0, // Show max 5 assurance tasks
    [UpdateCellType.SUGGESTED_ASSURANCE_TASK]: 3, // Show max 3 AI suggestions
    [UpdateCellType.TASK_CENTRIC_ASSURANCE_TASK]: 3, // Show max 3 task-centric AI suggestions
    [UpdateCellType.TABLE_TASK]: 1 // Show max 1 table task cell (it contains all tasks)
  };

  // Both views will be shown as separate modules

  // State to track if we're showing all entries for each type
  const [showAllEntries, setShowAllEntries] = useState<Record<UpdateCellType, boolean>>({
    [UpdateCellType.SOA_CONTROL]: false,
    [UpdateCellType.ASSURANCE_TASK]: false,
    [UpdateCellType.SUGGESTED_ASSURANCE_TASK]: false,
    [UpdateCellType.TASK_CENTRIC_ASSURANCE_TASK]: false,
    [UpdateCellType.TABLE_TASK]: false
  });


  // Function to refresh suggested tasks
  const fetchSuggestedTasksNow = async () => {
    setLoadingSuggestions(true);
    try {
      const suggestions = await getSuggestedTasks();
      console.log('[DEBUG] Refreshed AI suggestions:', suggestions);
      setSuggestedTasks(suggestions);
    } catch (error) {
      console.error('[DEBUG] Error refreshing AI-suggested tasks:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  var savedCount = 0;
  // Process an accepted task
  // const processAcceptedTask = useCallback(async (taskData: { suggestionDocId: string; task: any }) => {
  //   if (!taskData) return;
    
  //   console.log('[DEBUG] Processing accepted task:', taskData);
    
  //   try {
  //     // Create AI crumb with the format: suggestionDocId_taskId
  //     const aiCrumb = `${taskData.suggestionDocId}_${taskData.task.id}`;

  //     // Get current timestamp for task creation
  //     const now = new Date();
  //     const nextCheckDate = new Date(now.getTime() + (Number(taskData.task.checkInterval || 365) * 24 * 60 * 60 * 1000));

  //     // Get framework info and control IDs using our shared utility
  //     const { name: frameworkName, controls } = getFrameworkInfo(taskData.task.isoClauses);

  //     // Structure the task data to match the expected format in createTask
  //     const taskPayload = {
  //       checkDetail: {
  //         active: 'true',
  //         assignedTo: taskData.task.assignee || auth.currentUser?.uid || '',
  //         check: taskData.task.description || 'AI Suggested Task',
  //         evidence: taskData.task.evidenceType || 'Upload Evidence',
  //         framework: {
  //           '0': {
  //             frameName: frameworkName,
  //             controls: controls // Include the control IDs here
  //           }
  //         },
  //         meetingNeeded: taskData.task.meetingRequired?.toString() || 'true',
  //         aiSuggestionSource: aiCrumb
  //       },
  //       checkFrequency: {
  //         checkDays: taskData.task.checkInterval?.toString() || '365',
  //         reminderDays: taskData.task.reminderDays?.toString() || '7',
  //         sendReminder: taskData.task.meetingRequired?.toString() || 'true',
  //         nextCheck: nextCheckDate,
  //         lastCheck: {}
  //       },
  //       checkHistory: {
  //         numberPreviousChecks: '0'
  //       },
  //       createdBy: auth.currentUser?.uid || '',
  //       createdAt: FirebaseTimestamp.now(),
  //       updatedAt: FirebaseTimestamp.now()
  //     };

  //     console.log('[DEBUG] Creating task with data:', taskPayload);
  //     const result = await createTask(taskPayload);
      
  //     if (result?.success && result?.id) {
  //       console.log('[DEBUG] Accepting AI suggestion:', taskData);
  //       await acceptSuggestedTaskItem(taskData.suggestionDocId, taskData.task.id);
        
  //       // Refresh data
  //       fetchSuggestedTasksNow();
  //       fetchAssuranceTasks();
        
  //       toast.success('AI suggestion accepted and task created');
  //     }
  //   } catch (error) {
  //     console.error('[DEBUG] Error processing accepted task:', error);
  //     toast.error('Failed to process accepted task');
  //   } finally {
  //     setAcceptingTask(null);
  //   }
  // }, [fetchAssuranceTasks, fetchSuggestedTasksNow]);

  // Handle task saved from AddAssuranceTaskModal
  // const handleTaskSaved = useCallback(async (options: { suggestionDocId: string; task: any }) => {
  //   console.log('[DEBUG] Task saved, setting accepting task:', options);
  //   setAcceptingTask(options);
  // }, []);

  // Process task when acceptingTask changes
  // Effect to handle task processing when acceptingTask changes
  // useEffect(() => {
  //   if (acceptingTask) {
  //     processAcceptedTask(acceptingTask);
  //   }
  // }, [acceptingTask, processAcceptedTask]);

  // Function to fetch suggested tasks - prioritized for first load
  const fetchSuggestedTasks = useCallback(async () => {
    if (!activeTenancy?.id || !isAuthInitialized || !isAuthReady) return [];
    setLoadingSuggestions(true);
    try {
      const suggestions = await getSuggestedTasks();
      setSuggestedTasks(suggestions);
      setAiTasksLoaded(true);

      // Hide initial loading screen once AI tasks are loaded
      if (showInitialLoading) {
        setShowInitialLoading(false);
      }
    } catch (error) {
      console.error('[DEBUG] Error refreshing AI-suggested tasks:', error);
      setAiTasksLoaded(true); // Mark as loaded even on error to proceed
      if (showInitialLoading) {
        setShowInitialLoading(false);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, [activeTenancy?.id, isAuthInitialized, isAuthReady, showInitialLoading]);

  // Set up auth ready effect
  useEffect(() => {
    const unsubscribe = onAuthReady(() => {
      setIsAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  // Query for SOA updates data - delayed until AI tasks are loaded
  const {
    data: soaUpdatesData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['system-update', activeTenancy?.id, currentBatch],
    queryFn: async () => {
      if (!activeTenancy?.id || !isAuthInitialized || !isAuthReady) {
        throw new Error('Auth or tenant not ready');
      }

      const result = await getSOAUpdatesData(activeTenancy.id, currentBatch);
      console.log('[DEBUG] SOA Updates Data:', result);
      setControlsLoaded(true);
      return result;
    },
    enabled: !!activeTenancy?.id && isAuthInitialized && isAuthReady && aiTasksLoaded,
    staleTime: 60000, // 1 minute
  });

  // Priority loading: AI tasks first, then other data
  useEffect(() => {
    if (activeTenancy?.id && isAuthInitialized && isAuthReady && user) {
      // Load AI tasks first (this will hide the loading screen)
      fetchSuggestedTasks();

      // Load assurance tasks in background
      fetchAssuranceTasks();
    }
  }, [activeTenancy?.id, isAuthInitialized, isAuthReady, user, fetchSuggestedTasks]);

  useEffect(() => {
    if (activeTenancy?.id && isAuthInitialized && isAuthReady && user) {
      getAssignees(activeTenancy.id).then(result => {
        setAssignees(result);
      });
    }
  }, [activeTenancy?.id, isAuthInitialized, isAuthReady, user]);


  // Initialize active tabs for each control
  useEffect(() => {
    if (soaUpdatesData?.controls) {
      const newActiveTabs: Record<string, string> = {};
      soaUpdatesData.controls.forEach(control => {
        if (!activeTabs[control.controlID]) {
          newActiveTabs[control.controlID] = 'overview';
        }
      });

      if (Object.keys(newActiveTabs).length > 0) {
        setActiveTabs(prev => ({
          ...prev,
          ...newActiveTabs
        }));
      }
    }
  }, [soaUpdatesData?.controls]);

  // Filter controls based on search term and status
  const filteredControls = React.useMemo(() => {
    if (!soaUpdatesData?.controls) return [];

    return soaUpdatesData.controls.filter(control => {
      // Get the most relevant status, checking in order of preference
      const controlStatus = (
        control.controlData.currentImplementationStatus ||
        control.controlData.implementationStatus ||
        control.controlData.status ||
        control.currentImplementationStatus ||
        control.implementationStatus ||
        control.status ||
        'Not Implemented'
      ) as 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable' | string;

      // Debug log
      if (process.env.NODE_ENV === 'development') {
        console.log(`[DEBUG] Control ${control.controlID} status:`, {
          controlID: control.controlID,
          status: controlStatus,
          controlDataStatus: control.controlData.status,
          controlDataImplementationStatus: control.controlData.implementationStatus,
          controlDataCurrentImplementationStatus: control.controlData.currentImplementationStatus,
          rootStatus: control.status
        });
      }

      // Apply search term filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        control.controlID.toLowerCase().includes(searchLower) ||
        (control.controlData.title && control.controlData.title.toLowerCase().includes(searchLower)) ||
        (control.controlData.description && control.controlData.description.toLowerCase().includes(searchLower));

      // Apply status filter
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'Not Implemented' && (!controlStatus || controlStatus === 'Not Implemented')) ||
        controlStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [soaUpdatesData?.controls, searchTerm, statusFilter]);

  // Load data for the visible controls
  useEffect(() => {
    const loadControlData = async () => {
      if (!filteredControls.length || !activeTenancy?.id || !isAuthInitialized || !isAuthReady || !user) return;

      console.log('[DEBUG] Loading data for filtered controls:', filteredControls);

      for (const control of filteredControls) {
        const controlID = control.controlID;

        try {
          console.log(`[DEBUG] Loading data for control ${controlID}:`, control.controlData);

          // Load implementation plan
          const { db } = await import('@/js/external-js/config/main_config');
          const controlPath = `/customers/${activeTenancy.id}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism/${controlID}`;
          const docRef = await db.doc(controlPath).get();

          if (docRef.exists) {
            const docData = docRef.data() || {};
            console.log(`[DEBUG] Control ${controlID} document data:`, docData);

            if (docData.implementationPlan) {
              setImplementationPlans(prev => ({
                ...prev,
                [controlID]: docData.implementationPlan
              }));
            }
          } else {
            console.log(`[DEBUG] Control ${controlID} document does not exist in customer's collection`);
          }

          // Load assurance tasks
          const tasks = await loadAssuranceTasks(activeTenancy.id, controlID);
          console.log(`[DEBUG] Assurance tasks for ${controlID}:`, tasks);
          setAssuranceTasks(prev => ({
            ...prev,
            [controlID]: tasks
          }));

          // Load supporting docs
          const docs = await getSupportingDocs(activeTenancy.id, controlID);
          console.log(`[DEBUG] Supporting docs for ${controlID}:`, docs);
          setSupportingDocs(prev => ({
            ...prev,
            [controlID]: docs
          }));
        } catch (error) {
          console.error(`Error loading data for control ${controlID}:`, error);
        }
      }
    };

    loadControlData();
  }, [filteredControls, activeTenancy?.id, isAuthInitialized, isAuthReady, user]);

  // Handle saving implementation plan updates
  const handleSaveImplementationPlan = async (controlID: string) => {
    if (!activeTenancy?.id || !implementationPlans[controlID] || !isAuthInitialized || !isAuthReady || !user) return;

    try {
      const planText = implementationPlans[controlID];
      const success = await updateControlInSOA(activeTenancy.id, controlID, {
        implementationPlan: planText
      });

      if (success) {
        toast.success('Implementation plan saved successfully');
      } else {
        toast.error('Failed to save implementation plan');
      }
    } catch (error) {
      console.error('Error saving implementation plan:', error);
      toast.error('An error occurred while saving');
    }
  };

  // Handle accepting all changes for a control or refreshing data after task/suggestion updates
  const handleAcceptChanges = async (control: ControlData) => {
    // If we don't have a full control object, this is likely coming from a task or suggestion update
    if (!control || !control.controlID) {
      console.log('[DEBUG] Refresh triggered by task/suggestion update');

      // Refresh AI suggestions if we have any
      if (suggestedTasks.length > 0) {
        setLoadingSuggestions(true);
        try {
          const newSuggestions = await getSuggestedTasks();
          console.log(`[DEBUG] Refreshed AI suggestions: ${newSuggestions.length}`);
          setSuggestedTasks(newSuggestions);
        } catch (error) {
          console.error('[DEBUG] Error refreshing AI suggestions:', error);
        } finally {
          setLoadingSuggestions(false);
        }
      }

      // Refresh assurance tasks
      if (activeTenancy?.id && isAuthInitialized && isAuthReady && user) {
        setLoadingTasks(true);
        try {
          const allTasks = await getAllTasks();
          const activeTasks = allTasks.filter(task => task.checkDetail?.active === 'true');
          setActiveTasks(activeTasks);
        } catch (error) {
          console.error('[DEBUG] Error refreshing assurance tasks:', error);
        } finally {
          setLoadingTasks(false);
        }
      }

      // Refresh main SOA data
      refetch();
      return;
    }

    // Handle normal control changes
    if (!activeTenancy?.id || !isAuthInitialized || !isAuthReady || !user) {
      toast.error('No active tenancy selected or auth not ready');
      return;
    }

    try {
      const controlChangelog = control.controlData.changelog || [];
      const sortedChangelog = sortObjectKeys(controlChangelog);
      const success = await processControl(activeTenancy.id, control.controlID, JSON.stringify(sortedChangelog));

      if (success) {
        toast.success(`Changes for ${control.controlID} have been accepted`);
        refetch();
      } else {
        toast.error('Failed to process control changes');
      }
    } catch (error) {
      console.error('Error accepting changes:', error);
      toast.error('An error occurred while processing changes');
    }
  };

  // Handle loading more controls
  const handleLoadMore = () => {
    setCurrentBatch(prev => prev + 1);
  };

  // Handle refresh after accepting/rejecting tasks
  const handleRefresh = useCallback(() => {
    console.log('[DEBUG] Refreshing system update data');
    fetchSuggestedTasksNow();
    refetch();
    fetchAssuranceTasks();
  }, [fetchSuggestedTasksNow, refetch, fetchAssuranceTasks]);

  // Handle updating implementation plan text
  const handleImplementationPlanChange = (controlID: string, text: string) => {
    setImplementationPlans(prev => ({
      ...prev,
      [controlID]: text
    }));
  };

  // Handle tab change for a specific control
  const handleTabChange = (controlID: string, tabValue: string) => {
    setActiveTabs(prev => ({
      ...prev,
      [controlID]: tabValue
    }));
  };

  // Transform controls into update cell data
  const updateCells = useMemo(() => {
    if (!filteredControls && activeTasks.length === 0 && suggestedTasks.length === 0) return [];

    // Map all items first
    const controlCells = filteredControls.map(control => ({
      id: control.controlID,
      type: UpdateCellType.SOA_CONTROL,
      data: control
    } as UpdateCellData));

    const assuranceTaskCells = activeTasks.map(task => ({
      id: task.assID || `task-${Math.random().toString(36).substr(2, 9)}`,
      type: UpdateCellType.ASSURANCE_TASK,
      data: task
    } as UpdateCellData));

    // Regular document-centric suggested task cells
    const suggestedTaskCells = suggestedTasks.map(suggestion => ({
      id: suggestion.id || `suggestion-${Math.random().toString(36).substr(2, 9)}`,
      type: UpdateCellType.SUGGESTED_ASSURANCE_TASK,
      data: suggestion
    } as UpdateCellData));

    // Task-centric suggested task cells - each task gets its own cell
    const taskCentricSuggestedTaskCells = suggestedTasks.flatMap(suggestion => {
      // Extract individual tasks from each suggestion document
      const tasks = Array.isArray(suggestion.suggested_tasks) ?
        suggestion.suggested_tasks.filter((t: any) => {
          // Only include tasks that haven't been accepted/rejected yet
          return !t.status || (t.status !== 'accepted' && t.status !== 'rejected');
        }) : [];

      // Create a separate cell for each individual task
      return tasks.map((task: any, index: number) => ({
        id: `${suggestion.id}-task-${index}` || `task-centric-${Math.random().toString(36).substr(2, 9)}`,
        type: UpdateCellType.TASK_CENTRIC_ASSURANCE_TASK,
        data: {
          ...suggestion,
          currentTask: task,
          suggestionDocId: suggestion.id
        }
      }));
    });

    // Apply limits based on configuration
    const limitedControlCells = showAllEntries[UpdateCellType.SOA_CONTROL]
      ? controlCells
      : controlCells.slice(0, maxEntriesConfig[UpdateCellType.SOA_CONTROL]);

    const limitedAssuranceTaskCells = showAllEntries[UpdateCellType.ASSURANCE_TASK]
      ? assuranceTaskCells
      : assuranceTaskCells.slice(0, maxEntriesConfig[UpdateCellType.ASSURANCE_TASK]);

    const limitedSuggestedTaskCells = showAllEntries[UpdateCellType.SUGGESTED_ASSURANCE_TASK]
      ? suggestedTaskCells
      : suggestedTaskCells.slice(0, maxEntriesConfig[UpdateCellType.SUGGESTED_ASSURANCE_TASK]);

    const limitedTaskCentricCells = showAllEntries[UpdateCellType.TASK_CENTRIC_ASSURANCE_TASK]
      ? taskCentricSuggestedTaskCells
      : taskCentricSuggestedTaskCells.slice(0, maxEntriesConfig[UpdateCellType.TASK_CENTRIC_ASSURANCE_TASK]);

    // Create a single table task cell that contains all suggested tasks
    const tableTaskCell = [
      {
        id: `table-task-cell`,
        type: UpdateCellType.TABLE_TASK,
        data: {
          suggestions: suggestedTasks
        }
      } as UpdateCellData
    ];

    const limitedTableTaskCells = showAllEntries[UpdateCellType.TABLE_TASK]
      ? tableTaskCell
      : tableTaskCell.slice(0, maxEntriesConfig[UpdateCellType.TABLE_TASK]);

    // Combine all types of cells
    return [
      ...limitedControlCells,
      ...limitedAssuranceTaskCells,
      ...limitedSuggestedTaskCells,
      ...limitedTaskCentricCells,
      ...limitedTableTaskCells
    ];
  }, [filteredControls, activeTasks, suggestedTasks, showAllEntries, maxEntriesConfig]);

  // Log database debug information to console
  useEffect(() => {
    if (soaUpdatesData) {
      console.log('[DEBUG] Database Summary:', {
        tenant: activeTenancy?.id,
        batch: currentBatch,
        controlCount: soaUpdatesData.controls.length,
        filteredControlCount: filteredControls.length,
        processedControls: soaUpdatesData.processedControls,
        hasMoreControls: soaUpdatesData.hasMoreControls
      });
    }
  }, [soaUpdatesData, filteredControls, activeTenancy?.id, currentBatch]);

  // Show duck loading screen initially
  if (showInitialLoading) {
    return (
      <DuckLoadingScreen
        title="Loading System Updates..."
        text="Prioritizing AI suggestions for faster processing"
        duckFacts={[
          "Loading AI-suggested tasks first...",
          "Preparing your intelligent task recommendations...",
          "Optimizing your workflow with AI insights...",
          "Just a moment while we fetch your AI suggestions...",
          "Smart loading: AI tasks first, controls second!"
        ]}
      />
    );
  }

  return (
    <div className="main-container">
      {/* Unified Table Processing Section - Both AI Suggestions and SOA Controls */}
      <div className="page-container">
        <div className="glass-card">
          <div className="header-row flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="title flex flex-row">
                System Updates
              </h2>
              <p className="subtitle">
                This is a centralised view of all system updates and controls.
              </p>
            </div>
          </div>
          {activeTenancy?.id && (
            <UnifiedTable
              tenancyId={activeTenancy.id}
              soaControls={filteredControls || []}
              controlsLoaded={controlsLoaded}
              onUpdate={handleRefresh}
              acceptAction={async (item) => {
                console.log('Accepting AI suggestion item:', item);
                // Set the accepting task state - this will trigger the useEffect to process it
                await processAcceptedTask({
                  suggestionDocId: item.sourceId,
                  task: item
                });
                localStorage.setItem('aiSuggestionAccepted', 'true');
                return Promise.resolve();
              }}
            />
          )}
        </div>
      </div>

      {/* Add Assurance Task Modal for accepting AI suggestions */}
      <AddAssuranceTaskModal
        open={addTaskModalOpen}
        onOpenChange={setAddTaskModalOpen}
        onTaskAdded={() => fetchAssuranceTasks()}
        initialForm={acceptingTask ? {
          check: acceptingTask.task.description || '',
          framework: Array.isArray(acceptingTask.task.isoClauses) ? acceptingTask.task.isoClauses : [],
          assignedTo: '',
          evidence: acceptingTask.task.evidence?.toLowerCase().includes('upload') ? 'Upload Evidence' : 'Sight Evidence',
          checkDays: '30',
          reminderDays: '7',
          meetingNeeded: 'false',
          status: 'true',
        } : undefined}
        // onTaskSaved={handleTaskSaved}
      />
    </div>
  );
};

export default SystemUpdate;
